import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Container
} from "@mui/material";
import React from "react";
import { Controller, FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import UserHint from "../../../components/display/UserHint";
import { GlobalDataContext } from "../../../contexts/GlobalData";
import { SEND_INBOX } from "../../../enums/PermissionTitles";
import usePut from "../../../hooks/usePut";
import useSnackbar from "../../../hooks/useSnackbar";
import { receiver, receiver_ids } from "../components/OpenConversation";
import { INBOX_MULTIPLE, OPEN_CONVO } from "../../../enums/ApiPaths";
import useUser from "../../../hooks/useUser";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useWatch } from "react-hook-form";

import ConfirmAndCancelButtons from "../../../components/buttons/ConfirmAndCancelButtons";
import CharCountField from "../../../components/form/CharCountField";
const sendAll = "SendAll";
const sendAllClinics = "SendAllClinics";
const sendAllUsers = "SendAllUsers";
const sendAllEmployees = "SendAllEmployees";
const sendPlural = "sendPlural"
const pluralSending = [
  {
    name: "all",
    label: sendAll
  },
  {
    name: "all_users",
    label: sendAllUsers,
  },
  {
    name: "all_employees",
    label: sendAllEmployees,
  },
  {
    name: "all_clinics",
    label: sendAllClinics,
  }
]

const pluralSendingAdmin = [
  {
    name: "all_users",
    label: sendAllUsers,
  },
  {
    name: "all_employees",
    label: sendAllEmployees,
  },
]

export default function NewConversation({
  error,
  isLoading,
  users = [],
  profileId,
  handleClose,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleSnackbar } = useSnackbar();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const reciverId = searchParams.get(receiver) ?? null;
  const apiPath =
    searchParams.get(sendPlural) != null
      ? `${INBOX_MULTIPLE}?value=${searchParams.get(sendPlural)}`
      : OPEN_CONVO;
  const put = usePut(apiPath, ["conversations"], "conversations");
  const { t } = useTranslation();
  const { roles } = useUser();

  const methods = useForm({
    defaultValues: {
      message: "",
      recipient: null,
      sendPlural: searchParams.get(sendPlural) ?? null,
    },
    mode: "onChange",
  });

  const getReceiverIds = (recipient) => {
    if (Array.isArray(recipient)) {
      return recipient.map((r) => r.id);
    }
    return recipient ? [recipient.id] : [];
  };

  const getAutocompleteValue = (value) => {
    if (Array.isArray(value)) return value;
    return value ? [value]: [];
  };

  async function onSubmit(data) {
    if (!isPermissionAllowed(SEND_INBOX)) {
      handleSnackbar("YouDoNotHavePermissionToSendMessages", "error");
    }

    const dto = {
      message: data?.message,
      id: searchParams.get(sendPlural) != null ? null : reciverId,
      receiverIds: getReceiverIds(data?.recipient),
    };

    const res = await put.submitCallback(dto);
    if (res) {
      handleClose();
    }
  }

  function setReciver(value) {
    const ids = getReceiverIds(value);
    if (Number(ids.length) === 1) {
      searchParams.set(receiver, ids[0]);
    } else {
      searchParams.delete(receiver);
    }
    searchParams.set(receiver_ids, ids);
    setSearchParams(searchParams);
  }

  function handleCheckboxes(val) {
    if (searchParams.get(sendPlural) == val) {
      methods.setValue(sendPlural, null);
      searchParams.delete(sendPlural);
      setSearchParams(searchParams);
    } else {
      methods.setValue(sendPlural, val);
      searchParams.set(sendPlural, val);
      setSearchParams(searchParams);
    }
  }

  React.useEffect(() => {
    if (profileId && users.length > 0) {
      searchParams.set(receiver, profileId);
      setSearchParams(searchParams);
      const recipient = users.find((user) => Number(user?.id) === Number(profileId));
      methods.setValue("recipient", recipient);
    }
  }, [profileId, users, isLoading]);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        style={{ display: "flex", flexDirection: "column", flex: 1 }}
      >
        <DialogTitle>{t("SendMessage")}</DialogTitle>

        <DialogContent sx={{ flex: 1, overflowY: "auto" }}>
          {error ? (
            <Alert severity="error">{t("FailedToLoadRecipients")}</Alert>
          ) : (
            <Stack spacing={3} mt={1}>
              <Checkboxes
                handleCheckboxes={(val) => handleCheckboxes(val)}
                componentList={
                  roles?.master ? pluralSending : pluralSendingAdmin
                }
              />
              <Stack spacing={1}>
                <UserHint hint={"UsersCantReplyToYourMessages"} />
                <UserHint hint={"NotCreateGroupChat"} sx={{ mt: -2 }} />
              </Stack>

              <Controller
                name="recipient"
                control={methods.control}
                disabled={searchParams.get(sendPlural) != null}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    onChange={(_, value) => {
                      setReciver(value);
                      field.onChange(value);
                    }}
                    multiple
                    disableCloseOnSelect
                    options={users}
                    getOptionLabel={(option) =>
                      `${option?.fullName ?? ""} ${option?.email ?? ""}`.trim()
                    }
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    loading={isLoading}
                    value={getAutocompleteValue(field.value)}
                    renderOption={(propsInner, option, { selected }) => {
                      return (
                        <li {...propsInner} key={String(option.id)}>
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <Checkbox
                              edge="start"
                              checked={selected}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText
                            id={`checkbox-list-label-${option.id}`}
                            primary={`${option?.fullName ?? ""} ${
                              option?.email ?? ""
                            }`.trim()}
                          />
                        </li>
                      );
                    }}
                    renderInput={(params) => {
                      const { InputProps, ...otherParams } = params;
                      const { startAdornment, ...otherInputProps } = InputProps;
                      return (
                        <TextField
                          {...otherParams}
                          placeholder={
                            searchParams.get(receiver) ||
                            searchParams.get(receiver_ids)
                              ? ""
                              : t("SelectRecipient")
                          }
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            "data-testid": "inbox.new-conversation.recipient-input",
                            startAdornment: (
                              <Stack
                                direction="row"
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ maxHeight: "100px", overflowY: "auto" }}
                              >
                                {startAdornment}
                              </Stack>
                            ),
                            endAdornment: (
                              <>
                                {isLoading && <CircularProgress size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      );
                    }}
                    clearText={t("Clear")}
                    closeText={t("Close")}
                    openText={t("Open")}
                  />
                )}
              />
              <Controller
                name="message"
                control={methods.control}
                rules={{ required: true }}
                render={({ field }) => (
                  <CharCountField
                    {...field}
                    label={t("Message")}
                    multiline
                    rows={6}
                    inputProps={{ maxLength: 5000 }}
                    fullWidth
                    variant="outlined"
                    testId="inbox.new-conversation.message-input"
                  />
                )}
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <ConfirmAndCancelButtons
            confirmTitle="Send"
            cancelTitle="Cancel"
            confirmButtonProps={{
              disabled: !methods.formState.isValid,
            }}
            onCancel={handleClose}
            confirmButtonType="submit"
            cancelButtonType="button"
            testId="inbox.new-conversation"
          />
        </DialogActions>
      </form>
    </FormProvider>
  );
}

export function Checkboxes({componentList = [], handleCheckboxes}) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams();
  const {roles} = useUser();

  if (roles.user || roles.employee) {
    return null;
  }

  return (
      <Stack direction={"row"} alignItems={"center"} flexWrap="wrap">
        {componentList.map((component)=> (
          <Controller
            name={component.name}
            render={({ field }) => (
              <FormControlLabel
                label={t(component.label)}
                {...field}
                checked={searchParams.get(sendPlural) === component.name}
                control={<Checkbox defaultChecked={false} onClick={()=>handleCheckboxes(component.name)} />}
              />
            )}
          />
        ))}
      </Stack>     
    );
}
